import PropTypes from "prop-types";
import React from "react";
import { DragSource, DropTarget } from "react-dnd";
import { findDOMNode } from "react-dom";
import { enhanceContext } from "components/utils/enhance";

const Source = {
    beginDrag(props) {
        props.onBeginDrag(props.index);
        return {
            start: props.index,
            index: props.index,
        };
    },
    endDrag(props, monitor) {
        const { start, index } = monitor.getItem();
        props.moveComponent(start, index, true);
    },
};

const Target = {
    hover(props, monitor, component) {
        const dragIndex = monitor.getItem().index;
        const hoverIndex = props.index;

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
            return;
        }

        // Determine rectangle on screen
        const hoverBoundingRect = findDOMNode(component).getBoundingClientRect();

        // Get vertical middle
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

        // Determine mouse position
        const clientOffset = monitor.getClientOffset();

        // Get pixels to the top
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;

        // Only perform the move when the mouse has crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%

        let movedRows = true;

        // Dragging downwards
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
            movedRows = false;
        } else if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
            // drag upwards
            movedRows = false;
        }

        if (props.width) {
            const hoverClientX = clientOffset.x - hoverBoundingRect.left;
            const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
            // Dragging right
            if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
                return;
            } else if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
                // drag left
                return;
            }
        } else if (!movedRows) {
            // return if its not above or below a component that 100% width
            return;
        }

        // Time to actually perform the action
        props.moveComponent(dragIndex, hoverIndex);

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        monitor.getItem().index = hoverIndex;
    },
};

function targetCollect(connect) {
    return {
        connectDropTarget: connect.dropTarget(),
    };
}

function sourceCollect(connect, monitor) {
    return {
        connectDragSource: connect.dragSource(),
        connectDragPreview: connect.dragPreview(),
        isDragging: monitor.isDragging(),
    };
}

export default function (Component, enableDragDrop = false) {
    class ScollComponent extends Component {
        render() {
            const {
                x,
                y,
                width,
                dragDrop,
                connectDragSource,
                connectDropTarget,
                connectDragPreview,
                customDragSource,
            } = this.props;

            let content = (
                <div
                    style={{
                        position: "absolute",
                        zIndex: 1,
                        left: `${x}px`,
                        right: width ? "auto" : "0px",
                        top: `${y}px`,
                        contain: "layout",
                    }}
                >
                    {super.render()}
                </div>
            );

            if (enableDragDrop && dragDrop) {
                if (customDragSource) {
                    content = connectDragPreview(connectDropTarget(content));
                } else {
                    content = connectDragSource(connectDropTarget(content));
                }
            }

            return content;
        }
    }

    ScollComponent.contextTypes = {
        ...ScollComponent.contextTypes,
    };

    ScollComponent.propTypes = {
        ...ScollComponent.propTypes,
        index: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        x: PropTypes.number.isRequired,
        scrollLeft: PropTypes.number,
        width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        horizontalSpacing: PropTypes.number,
        moveComponent: PropTypes.func.isRequired,
        dragDrop: PropTypes.bool,
        customDragSource: PropTypes.bool,
    };

    ScollComponent.defaultProps = {
        ...ScollComponent.defaultProps,
        dragDrop: false,
        horizontalSpacing: 0,
        customDragSource: false,
        scrollLeft: 0,
    };

    if (enableDragDrop) {
        const DnDComponent = DropTarget(
            "component",
            Target,
            targetCollect
        )(DragSource("component", Source, sourceCollect)(ScollComponent));
        return enhanceContext(ScollComponent.contextTypes || {})(DnDComponent);
    }

    return ScollComponent;
}
